/** DIVETTA MILANO R7 — Search route controller and accessible predictive results. */
(() => {
  'use strict';

  const KEY = '__DivettaR7Search';
  window[KEY]?.destroy?.();

  const globalController = new AbortController();
  const { signal } = globalController;
  const roots = new Map();
  let unsubscribeFrame = null;

  const urlBase = () => {
    const origin = window.location.origin;
    return origin && origin !== 'null' ? origin : 'https://divetta.local';
  };

  const safeUrl = (value) => {
    try {
      const url = new URL(value, urlBase());
      const currentOrigin = window.location.origin;
      const sameOrigin = currentOrigin && currentOrigin !== 'null'
        ? url.origin === currentOrigin
        : url.protocol === 'file:' || url.origin === urlBase();
      return sameOrigin ? `${url.pathname}${url.search}${url.hash}` : '';
    } catch (_) {
      return '';
    }
  };

  const resourceLabel = (type) => ({
    product: 'Prodotto',
    collection: 'Collezione',
    page: 'Pagina',
    article: 'Journal',
    query: 'Ricerca'
  }[type] || 'Risultato');

  const resultImage = (item) => {
    if (!item) return '';
    if (typeof item.image === 'string') return item.image;
    if (item.image?.url) return item.image.url;
    if (typeof item.featured_image === 'string') return item.featured_image;
    if (item.featured_image?.url) return item.featured_image.url;
    return '';
  };

  const flattenResults = (payload) => {
    const results = payload?.resources?.results || payload?.resources || {};
    const ordered = [
      ['products', 'product'],
      ['collections', 'collection'],
      ['pages', 'page'],
      ['articles', 'article'],
      ['queries', 'query']
    ];
    return ordered.flatMap(([key, type]) => (Array.isArray(results[key]) ? results[key] : []).map((item) => ({ ...item, _type: type }))).slice(0, 10);
  };

  const mountRoot = (root) => {
    if (roots.has(root)) return;
    const local = new AbortController();
    const input = root.querySelector('[data-r7-search-input]');
    const form = root.querySelector('[data-r7-search-form]');
    const panel = root.querySelector('[data-r7-predictive]');
    const list = root.querySelector('[data-r7-predictive-list]');
    const status = root.querySelector('[data-r7-predictive-status]');
    if (!(input instanceof HTMLInputElement) || !form || !panel || !list) return;

    const state = {
      local,
      input,
      form,
      panel,
      list,
      status,
      timer: 0,
      closeTimer: 0,
      generation: 0,
      fetchController: null,
      activeIndex: -1,
      items: []
    };

    const close = () => {
      window.clearTimeout(state.timer);
      window.clearTimeout(state.closeTimer);
      state.timer = 0;
      state.closeTimer = 0;
      state.generation += 1;
      state.fetchController?.abort();
      state.fetchController = null;
      state.items.forEach((option) => option.setAttribute('aria-selected', 'false'));
      if (status) status.textContent = '';
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      state.activeIndex = -1;
      state.items = [];
    };

    const setActive = (index) => {
      if (!state.items.length) return;
      const next = ((index % state.items.length) + state.items.length) % state.items.length;
      state.activeIndex = next;
      state.items.forEach((option, optionIndex) => option.setAttribute('aria-selected', String(optionIndex === next)));
      const option = state.items[next];
      input.setAttribute('aria-activedescendant', option.id);
      option.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    };

    const render = (items, query) => {
      state.activeIndex = -1;
      input.removeAttribute('aria-activedescendant');
      list.replaceChildren();
      if (!items.length) {
        const item = document.createElement('li');
        item.className = 'r7-search__predictive-empty';
        item.textContent = `Nessun suggerimento per “${query}”. Premi Invio per cercare.`;
        list.append(item);
        panel.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        if (status) status.textContent = 'Nessun suggerimento. Premi Invio per vedere la ricerca completa.';
        state.items = [];
        return;
      }

      items.forEach((item, index) => {
        const href = safeUrl(item.url || item.styled_text_url || '');
        if (!href) return;
        const option = document.createElement('li');
        option.id = `${list.id}-Option-${index + 1}`;
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');

        const link = document.createElement('a');
        link.href = href;
        link.tabIndex = -1;

        const imageUrl = resultImage(item);
        if (imageUrl) {
          const image = document.createElement('img');
          image.src = imageUrl;
          image.alt = '';
          image.loading = 'lazy';
          image.width = 72;
          image.height = 72;
          link.append(image);
        } else {
          const placeholder = document.createElement('span');
          placeholder.setAttribute('aria-hidden', 'true');
          placeholder.textContent = '✦';
          link.append(placeholder);
        }

        const copy = document.createElement('span');
        const kind = document.createElement('small');
        kind.textContent = resourceLabel(item._type);
        const title = document.createElement('strong');
        title.textContent = item.title || item.text || query;
        copy.append(kind, title);

        const arrow = document.createElement('span');
        arrow.setAttribute('aria-hidden', 'true');
        arrow.textContent = '↗';
        link.append(copy, arrow);
        option.append(link);
        option.addEventListener('pointermove', () => setActive(state.items.indexOf(option)), { signal: local.signal });
        list.append(option);
      });

      state.items = Array.from(list.querySelectorAll('[role="option"]'));
      panel.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      if (status) status.textContent = `${state.items.length} suggerimenti disponibili.`;
    };

    const fetchResults = async (query, generation) => {
      if (local.signal.aborted || generation !== state.generation) return;
      const endpoint = form.dataset.r7PredictiveUrl;
      if (!endpoint) return;
      const request = new AbortController();
      state.fetchController = request;
      const isCurrent = () => !local.signal.aborted && !request.signal.aborted &&
        generation === state.generation && input.value.trim() === query;
      try {
        const url = new URL(endpoint, urlBase());
        if (url.origin !== urlBase()) throw new Error('Unexpected predictive search origin');
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product,collection,page,article,query');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('resources[limit_scope]', 'each');
        const response = await fetch(url, { headers: { Accept: 'application/json' }, signal: request.signal });
        if (!response.ok) throw new Error(`Predictive search ${response.status}`);
        const payload = await response.json();
        // Aborting a request alone does not invalidate a late body/queued callback.
        if (isCurrent()) render(flattenResults(payload), query);
      } catch (error) {
        if (error.name === 'AbortError' || !isCurrent()) return;
        close();
        if (status) status.textContent = 'I suggerimenti non sono disponibili. La ricerca completa continua a funzionare.';
      } finally {
        if (state.fetchController === request) state.fetchController = null;
      }
    };

    input.addEventListener('input', () => {
      close();
      const query = input.value.trim();
      if (query.length < 2) return;
      const generation = state.generation;
      state.timer = window.setTimeout(() => fetchResults(query, generation), 180);
    }, { signal: local.signal });

    input.addEventListener('keydown', (event) => {
      if (event.isComposing) return;
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (!state.items.length) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActive(state.activeIndex + 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActive(state.activeIndex < 0 ? state.items.length - 1 : state.activeIndex - 1);
      } else if (event.key === 'Enter' && state.activeIndex >= 0) {
        event.preventDefault();
        state.items[state.activeIndex]?.querySelector('a')?.click();
      }
    }, { signal: local.signal });

    root.addEventListener('focusout', (event) => {
      const next = event.relatedTarget;
      if (next instanceof Node && root.contains(next)) return;
      window.clearTimeout(state.closeTimer);
      state.closeTimer = window.setTimeout(() => {
        if (!root.contains(document.activeElement)) close();
      }, 0);
    }, { signal: local.signal });

    root.querySelector('[data-search-trigger]')?.addEventListener('click', () => {
      const component = document.getElementById('search-modal');
      if (typeof component?.showDialog === 'function') component.showDialog();
      else {
        const dialog = component?.querySelector('dialog');
        if (dialog && !dialog.open) dialog.showModal();
      }
      window.setTimeout(() => component?.querySelector('input[type="search"]')?.focus(), 30);
    }, { signal: local.signal });

    state.close = close;
    roots.set(root, state);
  };

  const unmountRoot = (root) => {
    const state = roots.get(root);
    if (!state) return;
    state.close();
    state.local.abort();
    roots.delete(root);
  };

  const mount = (scope = document) => {
    const candidates = scope.matches?.('[data-r7-search-root]') ? [scope] : Array.from(scope.querySelectorAll?.('[data-r7-search-root]') || []);
    candidates.forEach(mountRoot);
    Array.from(roots.keys()).forEach((root) => { if (!root.isConnected) unmountRoot(root); });
  };

  const update = () => {
    roots.forEach((_state, root) => {
      const rect = root.getBoundingClientRect();
      const viewport = Math.max(window.innerHeight, 1);
      const progress = Math.min(1, Math.max(0, -rect.top / Math.max(1, rect.height - viewport * .25)));
      root.style.setProperty('--r7-search-progress', progress.toFixed(4));
    });
  };

  const refresh = (scope = document) => {
    mount(scope);
    window.DivettaZorge?.refresh?.();
    window.DivettaZorge?.requestFrame?.();
  };

  if (window.DivettaZorge?.subscribe) unsubscribeFrame = window.DivettaZorge.subscribe('search', update);
  else window.addEventListener('scroll', update, { passive: true, signal });
  window.addEventListener('resize', update, { passive: true, signal });
  document.addEventListener('shopify:section:load', (event) => refresh(event.target), { signal });
  document.addEventListener('shopify:section:unload', (event) => {
    Array.from(roots.keys()).forEach((root) => {
      if (event.target === root || event.target.contains?.(root)) unmountRoot(root);
    });
  }, { signal });
  document.addEventListener('shopify:section:reorder', () => refresh(document), { signal });
  document.addEventListener('shopify:section:select', (event) => refresh(event.target), { signal });
  document.addEventListener('shopify:block:select', (event) => refresh(event.target), { signal });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => refresh(document), { once: true, signal });
  else refresh(document);

  window[KEY] = {
    mount,
    refresh,
    destroy() {
      globalController.abort();
      unsubscribeFrame?.();
      Array.from(roots.keys()).forEach(unmountRoot);
      delete window[KEY];
    }
  };
})();
